// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useState, useCallback, useEffect, useRef, Fragment } from 'react'
import { Loader2, FileCheck2, Package, Truck, CheckCircle2, XCircle, Send, FileText, Search, History } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { formatUsDate } from '../../api/format'
import { type PickedEntity } from './EntityPicker'
import { claimDocumentTab, openTherapyDocument, resolveTherapyForDocument } from './therapyDocuments'
import { usePharmacy } from '../../auth/PharmacyContext'
import EmailPicker, { isEmail, type EmailChoice } from './EmailPicker'
import SearchableSelect from '../../components/common/SearchableSelect'
import { usePaged, Pager, DEFAULT_PAGE_SIZE, preferredPageSize, rememberPageSize } from '../../components/Pager'
import { useTableFunnels, useServerFunnels, type FunnelColumn, type ServerFunnelColumn } from '../../components/common/TableFunnels'
import PatientLanguageBadge from '../../components/ui/PatientLanguageBadge'
import CareRxClinicalReviewDetail from './CareRxClinicalReviewDetail'
import DateInput from '../../components/ui/DateInput'
import InAppSignDialog from '../../components/carerx/InAppSignDialog'
import SignedSoFarLane from '../../components/carerx/SignedSoFarLane'
import { useCreateInAppEnvelope, useCareRxSigning } from '../../hooks/queries/inappEsign'
import { allowsDocuSign, allowsInApp, isInAppEnvelopeId, SIGNING_WORDS, type SigningMode } from '../../api/inappEsign'
import { esignErrorText } from '../../api/esignErrors'
import { DocuSignNotSetUpNote, DocuSignOffNote } from '../../components/carerx/SigningNotes'
import Notice from '../../components/ui/Notice'
import { StatTile } from '../../components/ui/Section'
import { docTrackerRequest, nextDocTile, type DocTileKey } from './docTrackerTiles'
import Box from '@mui/material/Box'
import AppDialog from '../../components/ui/AppDialog'
import AppButton from '../../components/ui/AppButton'
import { AppTextField } from '../../components/ui/FormFields'

// D-UI-2: funnel columns, keyed by the header label each table already maps over.
const AWAITING_FUNNELS: FunnelColumn<api.CareRxAwaitingRow>[] = [
  { key: 'Recipient', label: 'Recipient', value: a => a.recipient_name || a.recipient_email || '—' },
  { key: 'Subject', label: 'Subject', value: a => a.subject_type },
  { key: 'Status', label: 'Status', value: a => `${a.status}${a.aging ? ` · aging (${a.days_out}d)` : ''}` },
  { key: 'Sent', label: 'Sent', value: a => a.sent_at || a.created_at || null },
]
const FILL_FUNNELS: FunnelColumn<any>[] = [
  { key: 'Drug', label: 'Drug', value: f => f.drug || '—' },
  { key: 'Qty / Lot', label: 'Qty / Lot', value: f => `${f.quantity || '—'}${f.lot ? ` · ${f.lot}` : ''}` },
  { key: 'Status', label: 'Status', value: f => String(f.fill_status ?? '').replace(/\b\w/g, c => c.toUpperCase()) },
  { key: 'Tracking', label: 'Tracking', value: f => f.carrier ? `${f.carrier}${f.tracking_number ? ' · ' + f.tracking_number : ''}` : '—' },
]

const CONSENT_TYPES: { key: string; label: string }[] = [
  { key: 'treatment', label: 'Consent to treat' },
  { key: 'financial', label: 'Financial responsibility' },
  { key: 'hipaa', label: 'HIPAA authorization' },
  { key: 'aob', label: 'Assignment of benefits' },
]
// 'awaiting_wet_signature' fits carerx_consent.status NVARCHAR(24); 'awaiting_physical_signature' (27) failed every save
const C_STATUS = ['pending', 'awaiting_wet_signature', 'obtained', 'declined', 'revoked']
/** Not yet signed: 'pending', or an e-consent clinical approval made ready but nobody sent (D-CRX-59 R-3). */
const unsigned = (s: string) => s === 'pending' || s === 'ready_to_send'
const C_METHOD = ['', 'verbal', 'written', 'esign']
type CRow = { type: string; label: string; id?: string; status: string; method: string; expires_at: string; notes: string }

// D-CRX-59: one chip vocabulary for all three document lanes.
const laneChip = (s: string): { label: string; fg: string; bg: string } =>
  s === 'signed' || s === 'obtained' ? { label: 'Signed', fg: '#15803d', bg: 'rgba(22,163,74,.12)' }
  : s === 'sent' || s === 'pending' ? { label: 'Sent', fg: '#0369a1', bg: 'rgba(3,105,161,.12)' }
  : s === 'ready_to_send' ? { label: 'Ready to send', fg: '#b45309', bg: 'rgba(217,119,6,.14)' }
  : s === 'draft' ? { label: 'Draft', fg: '#475569', bg: 'var(--color-bg)' }
  : s === 'declined' || s === 'voided' ? { label: s, fg: '#b91c1c', bg: 'rgba(220,38,38,.10)' }
  : { label: '—', fg: 'var(--color-text-mute)', bg: 'transparent' }

// D-UI-2: the document tracker is SERVER-paged, so its funnels only collect what was asked for
// and the endpoint applies them. The lane options are the chips laneChip draws, under the labels
// it draws them with; the endpoint folds a lane's codes the same way (pending reads "Sent").
const LANE_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'ready_to_send', label: 'Ready to send' },
  { value: 'sent', label: 'Sent' },
  { value: 'signed', label: 'Signed' },
]
const DOC_TRACKER_FUNNELS: ServerFunnelColumn[] = [
  { key: 'patient', label: 'Patient', kind: 'text' },
  { key: 'medication', label: 'Medication', kind: 'text' },
  { key: 'enrollment_lane', label: 'Enrollment form', kind: 'list', options: LANE_OPTIONS },
  { key: 'consent_lane', label: 'Patient consent', kind: 'list',
    options: [...LANE_OPTIONS, { value: 'declined', label: 'declined' }, { value: 'none', label: '—' }] },
  { key: 'pot_lane', label: 'Plan of Treatment', kind: 'list',
    options: [...LANE_OPTIONS, { value: 'visit_open', label: '— (visit open · form not started)' }, { value: 'not_started', label: '— (no visit yet)' }] },
]

/**
 * D-CRX-59 R-5 + 59b: the per-Plan-of-Treatment DOCUMENT TRACKER. Three lanes per therapy —
 * Enrollment (pharmacist + provider, ready after clinical review), Consent (patient / nurse §I /
 * interpreter §J, ready after clinical review), POT (physician, ready when the nurse attests).
 * Every draft is visible from therapy birth; Send only lights up on ready_to_send.
 */
function DocTrackerPanel({ onPickPatient }: { onPickPatient: (p: PickedEntity) => void }) {
  const { activePharmacy } = usePharmacy()
  const [rows, setRows] = useState<api.CareRxDocTrackerRow[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  // Send-confirm dialog: the recipient is shown and editable BEFORE the envelope goes to DocuSign,
  // and (consent only) the edited patient email can be saved as a durable CareRx contact override.
  const [confirm, setConfirm] = useState<SendConfirm | null>(null)
  // Void dialog: only offered on an in-process lane; a reason is required and audited.
  const [voidFor, setVoidFor] = useState<{ key: string; envelopeId: string; label: string } | null>(null)
  // D-CRX-81 per-envelope audit trail: opens a drawer with our actions + live DocuSign events.
  const [auditFor, setAuditFor] = useState<{ envelopeId: string; label: string } | null>(null)
  // Enrollment send: choose the pharmacist + set signing order (provider first / pharmacist first).
  // replaceEnvelopeId set = Resend: the same dialog, and the open envelope is replaced (go-live #11).
  const [enrollFor, setEnrollFor] = useState<{ key: string; therapyId: string; providerEmail: string; providerName: string; prescriberId: string | null; replaceEnvelopeId?: string } | null>(null)
  // ASP 2026-10-11 (#15): the clinical review behind this Plan of Treatment, opened in place.
  const [reviewFor, setReviewFor] = useState<string | null>(null)
  // D-CRX-87 §4d / D-ESIGN-1: the pharmacy's signing mode (docusign default | in_app | both), whether DocuSign is
  // set up for it, and the in-app envelope being signed / followed. Owner ruling 2026-10-21 (latest): "Sign in app"
  // is offered on the enrollment form only (the pharmacist signs in OwlRx). Under in_app, consent and the Plan of
  // Treatment cannot be sent (the server refuses 409); their Send is replaced by a note. Existing envelopes keep
  // their Check / Void / Audit in every mode.
  const signing = useCareRxSigning()
  const signingMode = signing.mode
  const docusignOn = allowsDocuSign(signingMode)
  const createInApp = useCreateInAppEnvelope()
  const [signFor, setSignFor] = useState<string | null>(null)
  // SERVER-SIDE search + paging (D-CRX-73 review). The table used to fetch a flat TOP 200 and page
  // it in the browser: the 201st Plan of Treatment was unreachable with nothing on screen admitting
  // it existed. `search` is what the operator is typing; `q` is the debounced value that actually
  // goes to the server, so a five-letter name is one request instead of five.
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  const fx = useServerFunnels(DOC_TRACKER_FUNNELS)
  // CareRx wave-1 #2: a clicked dashboard tile adds its lane predicates to the funnels' (docTrackerTiles.ts).
  const [tile, setTile] = useState<DocTileKey | null>(null)
  const { filter, tile: tileParam, sig } = docTrackerRequest(fx.filter, tile)
  const filtered = fx.active || tile !== null
  // The page belongs to the funnel selection it was chosen under, so a new selection reads page 1
  // in the SAME render: one request for it, not one for the old page and then one for page 1.
  const [pageAt, setPageAt] = useState({ sig: '', page: 1 })
  const page = pageAt.sig === sig ? pageAt.page : 1
  const setPage = (n: number) => setPageAt({ sig, page: n })
  const [pageSize, setPageSizeRaw] = useState(() => preferredPageSize())
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [summary, setSummary] = useState<api.CareRxDocTrackerSummary>(
    { drafts: 0, ready_to_send: 0, awaiting_signature: 0, fully_signed: 0 })
  // The page size is a sticky per-user preference shared with every other table (Pager.tsx).
  const setPageSize = (n: number) => { rememberPageSize(n); setPageSizeRaw(n); setPage(1) }

  useEffect(() => {
    const id = setTimeout(() => { setQ(search.trim()); setPage(1) }, 300)
    return () => clearTimeout(id)
  }, [search])

  // Ticking two funnel values in a row is two requests in flight. Only the LAST one asked for may
  // land, or the rows on screen would answer an older selection than the chips above them show.
  const reqSeq = useRef(0)
  const reload = useCallback(() => {
    const seq = ++reqSeq.current
    setLoading(true)
    api.listCareRxDocTracker({ q: q || undefined, page, page_size: pageSize, filter, tile: tileParam })
      .then(d => {
        if (seq !== reqSeq.current) return
        setRows(d.items ?? [])
        setTotal(d.total ?? d.items?.length ?? 0)
        setTotalPages(d.total_pages ?? 1)
        // An empty page carries no window values (the server sends zeros), so under a tile filter it
        // keeps the tiles it had rather than zeroing the stable summary.
        if (d.summary && ((d.items?.length ?? 0) > 0 || !tile)) setSummary(d.summary)
        // The total rides along on a ROW (a window function), so an empty page cannot report one.
        // Land past the end — rows deleted or sent while you were on page 3 — and the screen would
        // otherwise sit empty and dead, claiming "0 plans" over a table that still has them.
        if ((d.items?.length ?? 0) === 0 && page > 1) setPage(1)
      })
      .catch(e => { if (seq === reqSeq.current) setNote(friendlyError(e)) })
      .finally(() => { if (seq === reqSeq.current) setLoading(false) })
  }, [q, page, pageSize, sig])   // eslint-disable-line react-hooks/exhaustive-deps -- sig stands for filter and for setPage
  useEffect(() => { reload() }, [reload])

  const act = async (key: string, fn: () => Promise<unknown>, okMsg: string) => {
    setBusy(key); setNote(null)
    try { await fn(); setNote(okMsg); reload() }
    catch (e) { setNote(esignErrorText(e)) }
    finally { setBusy(null) }
  }

  // Runs the confirmed send. If the operator chose to persist (consent only), the CareRx contact
  // override is written first so it also survives the next send — then the envelope goes out with
  // the (possibly edited) recipient.
  const submitConfirm = (email: string, interpreter: string, persist: boolean) => {
    if (!confirm) return
    const c = confirm
    setConfirm(null)
    act(c.key, async () => {
      if (persist && c.patientId) await api.addContactEmail('patient', c.patientId, email).catch(() => {})
      return c.run(email, interpreter || undefined)
    }, c.okMsg(email))
  }

  const submitVoid = (reason: string) => {
    if (!voidFor) return
    const v = voidFor
    setVoidFor(null)
    act(v.key, () => api.voidCareRxEsign(v.envelopeId, reason), `${v.label} envelope voided.`)
  }

  const submitEnroll = (providerEmail: string, pharmacistEmail: string, pharmacistName: string, providerFirst: boolean, providerSave: boolean) => {
    if (!enrollFor) return
    const e = enrollFor
    setEnrollFor(null)
    const order = providerFirst ? 'provider → pharmacist' : 'pharmacist → provider'
    act(e.key, async () => {
      if (providerSave && e.prescriberId) await api.addContactEmail('prescriber', e.prescriberId, providerEmail).catch(() => {})
      return api.sendCareRxEnrollmentEsign(e.therapyId, {
        provider_email: providerEmail,
        provider_name: e.providerName || undefined,
        pharmacist_email: pharmacistEmail || undefined,
        pharmacist_name: pharmacistName || undefined,
        provider_first: providerFirst,
        replace_envelope_id: e.replaceEnvelopeId,
      })
    }, e.replaceEnvelopeId ? `Enrollment form re-sent (${order}, fresh envelope).` : `Enrollment form sent (${order}).`)
  }

  // D-ESIGN-1: "Sign in app" — nothing is emailed; the envelope opens for signing right here.
  const startInApp = (key: string, run: () => Promise<{ envelope_id: string; provider_on_portal?: boolean }>, okMsg: string) => {
    setBusy(key); setNote(null)
    run()
      .then(r => {
        setNote(r.provider_on_portal === false
          ? `${okMsg} The provider has no OwlRx login, so their part cannot be signed in app — send it with DocuSign.`
          : `${okMsg} The provider signs on their OwlRx provider portal ("Waiting for your signature").`)
        reload()
        setSignFor(r.envelope_id)
      })
      .catch(e => setNote(esignErrorText(e)))
      .finally(() => setBusy(null))
  }
  const submitEnrollInApp = (pharmacistAccountId: string, providerFirst: boolean) => {
    if (!enrollFor) return
    const e = enrollFor
    setEnrollFor(null)
    startInApp(e.key, () => createInApp.mutateAsync({ kind: 'enrollment', therapyId: e.therapyId, pharmacistAccountId, providerFirst }),
      'Enrollment form opened for signing in OwlRx.')
  }

  const Chip = ({ s, sub }: { s: string; sub?: string | null }) => {
    const c = laneChip(s)
    return (
      <div>
        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, color: c.fg, background: c.bg, whiteSpace: 'nowrap' }}>{c.label}</span>
        {sub && <div style={{ fontSize: 10.5, color: 'var(--color-text-mute)', marginTop: 3 }}>{sub}</div>}
      </div>
    )
  }

  // D-CRX-59c: every lane is OPENABLE. Signed lanes open the signed instrument from the chart —
  // fetched from DocuSign on first view if it hasn't landed yet. POT opens its generated PDF.
  //
  // UNSIGNED lanes open THIS THERAPY's generated draft — the same artifact TherapyDocumentButtons
  // opens on the enrollment queue, the therapy lanes and clinical review, via the one shared opener.
  // (Mario 2026-09-02: this tab used to call openCareRxTemplatePdf, which streams the active
  // UPLOADED BLOB from esign_doc_templates. That is the mechanism D-CRX-63/D-CRX-67 replaced: it is
  // not this patient's document, it carries whatever letterhead and whatever state's law the file
  // happened to have, and on a tenant with nothing uploaded it 404s `template_missing`. The Consent
  // tab was therefore the ONE screen showing a different — and wrong — PDF from every other screen.)
  const viewLane = async (key: string, t: api.CareRxDocTrackerRow, lane: 'enrollment' | 'consent' | 'pot') => {
    setBusy(key); setNote(null)
    try {
      if (lane === 'pot') {
        const doc = t.pot_signed_doc_id ?? t.pot_document_id
        if (doc) await api.openCareRxPdfDocument(doc)
      } else {
        const signedDoc = lane === 'enrollment' ? t.enrollment_signed_doc_id : t.consent_signed_doc_id
        const laneState = lane === 'enrollment' ? t.enrollment_lane
          : t.consent_status === 'obtained' ? 'signed' : t.consent_status
        const env = lane === 'enrollment' ? t.enrollment_envelope_id : t.envelope_id
        if (signedDoc) await api.openCareRxPdfDocument(signedDoc)
        else if (laneState === 'signed' && env) {
          const f = await api.fetchSignedCareRxEsign(env)   // first view: pull the signed PDF into the chart
          await api.openCareRxPdfDocument(f.document_id)
          reload()
        }
        else await openTherapyDocument(lane, t.therapy_id, activePharmacy ?? undefined)
      }
    } catch (e) {
      setNote(esignErrorText(e))
    } finally { setBusy(null) }
  }
  // A filed document by id (the in-app signed-so-far copy); no reload — nothing on the row changes.
  const openDoc = (key: string, docId: string) => {
    setBusy(key); setNote(null)
    api.openCareRxPdfDocument(docId).catch(e => setNote(esignErrorText(e))).finally(() => setBusy(null))
  }
  /**
   * What the button will ACTUALLY open, which is not the same question as what state the lane is
   * in. QA of #2604: the label read "View signed" whenever the lane said signed, but viewLane
   * falls through to the watermarked draft when there is neither a filed signed document nor an
   * envelope to fetch one from — so the button promised the executed instrument and served a copy
   * stamped "DRAFT — NOT FOR SIGNATURE". Promise what the click delivers.
   */
  const laneLabel = (lane: string | null | undefined, signedDocId: string | null | undefined, envelopeId: string | null | undefined) =>
    lane === 'signed' && (signedDocId || envelopeId) ? 'View signed' : 'View form'

  const viewBtn = (key: string, onClick: () => void, label = 'View') => (
    <button disabled={busy !== null} onClick={onClick}
      style={{ fontSize: 11, marginTop: 4, marginRight: 6, border: '1px solid var(--color-border)', borderRadius: 6, padding: '2px 8px', background: 'transparent', color: 'var(--color-text-soft)', cursor: 'pointer' }}>
      {busy === key ? '…' : label}</button>
  )
  const sendBtn = (key: string, onClick: () => void, disabled?: boolean, title?: string) => (
    <button disabled={busy !== null || disabled} title={title}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, fontWeight: 600, marginTop: 4, padding: '3px 9px', borderRadius: 7, cursor: disabled ? 'not-allowed' : 'pointer', border: '1px solid var(--color-primary)', color: disabled ? 'var(--color-text-mute)' : 'var(--color-primary)', background: 'transparent', opacity: disabled ? .55 : 1 }}
      onClick={onClick}>{busy === key ? <Loader2 className="spin" size={11} /> : <Send size={11} />} Send</button>
  )
  // Pull the latest signature status from DocuSign on demand. Needed when a live Connect webhook
  // can't reach the app (e.g. a local lab against DocuSign demo): after the recipient signs, this
  // flips the lane to 'signed'; the signed PDF is then filed into the chart on first view (fetch-signed).
  const checkBtn = (key: string, envelopeId: string) => (
    <button disabled={busy !== null} title="Pull the latest signature status from DocuSign"
      style={{ fontSize: 11, marginTop: 4, marginLeft: 6, border: '1px solid var(--color-border)', borderRadius: 6, padding: '2px 8px', background: 'transparent', color: 'var(--color-text-soft)', cursor: 'pointer' }}
      onClick={() => act(key, async () => {
        const r = await api.refreshCareRxEsign(envelopeId)
        return r
      }, 'Checked DocuSign — status updated.')}>
      {busy === key ? '…' : 'Check signature'}</button>
  )
  // D-ESIGN-1: an in-app envelope has no DocuSign to check — this opens it: who signs next, the document,
  // and (for the next signer) the signing form.
  const signBtn = (envelopeId: string) => (
    <button disabled={busy !== null} title="Open the in-app signature — who signs next, and sign if it is you"
      data-testid="inapp-sign-open"
      style={{ fontSize: 11, marginTop: 4, marginLeft: 6, border: '1px solid var(--color-primary)', borderRadius: 6, padding: '2px 8px', background: 'transparent', color: 'var(--color-primary)', cursor: 'pointer' }}
      onClick={() => setSignFor(envelopeId)}>Sign / status</button>
  )
  // Pulls the status from DocuSign for a DocuSign envelope; opens the in-app signature for an in-app one.
  const statusBtn = (key: string, envelopeId: string) => isInAppEnvelopeId(envelopeId) ? signBtn(envelopeId) : checkBtn(key, envelopeId)
  // Void is offered ONLY on an in-process lane (the caller guards on lane === 'sent'); a signed or
  // already-voided envelope is never voidable (the server also enforces this).
  const voidBtn = (key: string, envelopeId: string, label: string) => (
    <button disabled={busy !== null} title="Void this in-process signature (reason required)"
      style={{ fontSize: 11, marginTop: 4, marginLeft: 6, border: '1px solid #fecaca', borderRadius: 6, padding: '2px 8px', background: 'transparent', color: '#b91c1c', cursor: 'pointer' }}
      onClick={() => setVoidFor({ key, envelopeId, label })}>Void</button>
  )
  // Resend: offered on an in-process lane. Opens the lane's own send dialog so the recipient can be
  // corrected; the server replaces the open envelope only once the new send has passed every check.
  const resendBtn = (key: string, onClick: () => void) => (
    <button disabled={busy !== null} title="Send a fresh envelope and void the open one"
      style={{ fontSize: 11, marginTop: 4, marginLeft: 6, border: '1px solid var(--color-border)', borderRadius: 6, padding: '2px 8px', background: 'transparent', cursor: 'pointer' }}
      onClick={onClick}>{busy === key ? 'Resending…' : 'Resend'}</button>
  )
  // Audit trail: offered on any lane that has an envelope — who did what to the document, from our
  // audit_log AND live from DocuSign (viewed / signed / declined per recipient).
  const auditBtn = (envelopeId: string, label: string) => (
    <button title="Audit trail — who did what to this document"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, marginTop: 4, marginLeft: 6, border: '1px solid var(--color-border)', borderRadius: 6, padding: '2px 8px', background: 'transparent', color: 'var(--color-text-soft)', cursor: 'pointer' }}
      onClick={() => setAuditFor({ envelopeId, label })}><History size={11} /> Audit</button>
  )

  // While a funnel is set the panel stays mounted through a refetch: replacing it would close the
  // funnel menu on every tick and take away the chips that clear the filter.
  if (loading && !filtered) return <div style={muted}><Loader2 className="spin" size={15} /> Loading document tracker…</div>

  // Dashboard tiles — same lane vocabulary as the chips, and counted SERVER-SIDE over every row
  // matching the search rather than the page on screen. Counting the loaded page would have made
  // "Ready to send: 3" mean "3 on this page", which is a different and much less useful number.
  // Every tile counts PLANS and filters to exactly the plans it counted (docTrackerTiles.ts); the
  // total clears. Wave-1 #2: the lane tiles used to count documents, which no plan list could show.
  const tiles: { label: string; value: number; color: string; hint?: string; tile: DocTileKey | null }[] = [
    // summary.plans ignores the tile filter (total follows it); older API bundles omit it.
    { label: 'Plans of Treatment', value: summary.plans ?? total, color: 'var(--color-primary)', tile: null },
    { label: 'Drafts', value: summary.drafts, color: '#475569', hint: 'plans with a draft', tile: 'draft' },
    { label: 'Ready to send', value: summary.ready_to_send, color: '#b45309', hint: 'plans with one ready', tile: 'ready_to_send' },
    { label: 'Awaiting signature', value: summary.awaiting_signature, color: '#0369a1', hint: 'plans with one out', tile: 'sent' },
    { label: 'Fully signed', value: summary.fully_signed, color: '#15803d', hint: 'plans, all three signed', tile: 'fully_signed' },
  ]

  if (reviewFor) {
    return <CareRxClinicalReviewDetail therapyId={reviewFor} backLabel="Back to Consent & Drug Order Form" readOnly
      onBack={() => { setReviewFor(null); reload() }} />
  }

  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 10, padding: 12, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
        <FileText size={15} color="var(--color-primary)" /> Documents per Plan of Treatment ({total})
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
        {tiles.map(k => <StatTile key={k.label} label={k.label} value={k.value} color={k.color} hint={k.hint}
          active={tile === k.tile} onClick={() => setTile(cur => nextDocTile(cur, k.tile))} />)}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', marginBottom: 8 }}>
        Enrollment → pharmacist + provider after clinical review · Consent → patient (nurse §I joins on staffing, interpreter §J optional) · POT → physician after the nurse attests. Drafts are visible from day one; Send lights up when the phase completes.
      </div>
      {note && <div style={{ fontSize: 12, marginBottom: 8, color: /sent|Sent|signed/.test(note) ? 'var(--color-primary)' : '#b45309' }}>{note}</div>}
      {/* Search sits ON the table it filters, and filters in SQL — matching only the fetched page
          would quietly hide every match on a page nobody had loaded, which is worse than no search. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 280px', maxWidth: 380 }}>
          <Search size={14} aria-hidden="true"
            style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-mute)' }} />
          <input value={search} onChange={e => setSearch(e.target.value)} aria-label="Search plans of treatment"
            placeholder="Search patient, MRN, medication, Rx or prescriber…"
            style={{ width: '100%', boxSizing: 'border-box', fontSize: 13, padding: '6px 28px 6px 28px', borderRadius: 8,
                     border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text)' }} />
          {search && (
            <button onClick={() => setSearch('')} aria-label="Clear search"
              style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', border: 'none',
                       background: 'transparent', cursor: 'pointer', color: 'var(--color-text-mute)', lineHeight: 1, padding: 2 }}>
              <XCircle size={14} />
            </button>
          )}
        </div>
        {loading && <span style={{ fontSize: 12, color: 'var(--color-text-mute)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <Loader2 className="spin" size={13} /> Loading…</span>}
        {!loading && q && <span style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>
          {total} match{total === 1 ? '' : 'es'} for “{q}”</span>}
      </div>
      {fx.chips}
      {rows.length === 0 && !filtered ? (
        <div style={muted}>{q ? `No Plan of Treatment matches “${q}”.` : 'No active Plans of Treatment.'}</div>
      ) : (
        <>
        <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} label="plans" />
        <div style={{ overflowX: 'auto' }}><table {...fx.tableProps} className="rwd-stack" style={{ width: '100%', minWidth: 'max-content', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr>{DOC_TRACKER_FUNNELS.map(c => <th key={c.key} style={th}>{c.label} {fx.funnel(c.key)}</th>)}</tr></thead>
          <tbody>
            {/* The header stays when a funnel matches nothing, so the funnel that did it can be changed. */}
            {rows.length === 0 && <tr><td colSpan={5} style={{ ...td, color: 'var(--color-text-mute)' }}>{loading ? 'Loading…' : 'No Plan of Treatment matches the current filters.'}</td></tr>}
            {rows.map(t => {
              // Derived once, in SQL — the chip and the tiles now read the same value (see
              // CareRxDocTrackerRow.consent_lane).
              const consentLane = t.consent_lane
              return (
                <tr key={t.therapy_id} style={{ borderBottom: '1px solid var(--color-border)', verticalAlign: 'top' }}>
                  {/* The way in to a patient's consent rows and fill lane. It used to be a second
                      search box under this table (D-CRX-73 review, Mario 2026-09-02: "remove the
                      bottom search") — two search fields on one tab, the top one filtering the
                      table and the bottom one navigating away from it. Only the NAME is the target,
                      not the whole row: the three lanes to its right are full of View / Send /
                      Resend buttons, and a row-wide click would swallow every one of them. */}
                  <td data-label="Patient" style={td}>
                    <button onClick={() => onPickPatient({ id: t.patient_id, label: t.patient_name || 'Patient' })}
                      title={`Open ${t.patient_name || 'this patient'} — consents and fills`}
                      style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontWeight: 700,
                               color: 'var(--color-primary)', cursor: 'pointer', textAlign: 'left' }}>
                      {t.patient_name || '—'}<PatientLanguageBadge patientId={t.patient_id} />
                    </button>
                  </td>
                  <td data-label="Medication" style={td}>{t.drug_name || '—'}
                    <div><button onClick={() => setReviewFor(t.therapy_id)} title="Open the clinical review for this Plan of Treatment (locked once decided)"
                      style={{ fontSize: 11, marginTop: 4, border: '1px solid var(--color-border)', borderRadius: 6, padding: '2px 8px', background: 'transparent', cursor: 'pointer' }}>
                      Clinical review</button></div></td>
                  <td data-label="Enrollment form" style={td}>
                    <Chip s={t.enrollment_lane}
                      sub={t.enrollment_lane === 'draft' ? 'awaiting clinical review'
                        : t.enrollment_lane === 'sent' ? `pharmacist + provider · ${t.enrollment_sent_at ? formatUsDate(t.enrollment_sent_at) : ''}`
                        : t.enrollment_lane === 'signed' && t.enrollment_signed_at ? formatUsDate(t.enrollment_signed_at) : null} />
                    {/* D-ESIGN-1 O3: once one signer has signed in app, the lane opens the signed-so-far copy. */}
                    {t.enrollment_progress?.partial_doc_id
                      ? <SignedSoFarLane progress={t.enrollment_progress} busy={busy === `ven-${t.therapy_id}`} disabled={busy !== null}
                          onOpen={docId => openDoc(`ven-${t.therapy_id}`, docId)} />
                      : viewBtn(`ven-${t.therapy_id}`, () => viewLane(`ven-${t.therapy_id}`, t, 'enrollment'),
                          laneLabel(t.enrollment_lane, t.enrollment_signed_doc_id, t.enrollment_envelope_id))}
                    {t.enrollment_lane === 'ready_to_send' && sendBtn(`en-${t.therapy_id}`, () => setEnrollFor({
                      key: `en-${t.therapy_id}`,
                      therapyId: t.therapy_id,
                      providerEmail: t.prescriber_email || '',
                      providerName: t.prescriber_name || '',
                      prescriberId: t.prescriber_id,
                    }))}
                    {t.enrollment_lane === 'sent' && t.enrollment_envelope_id && statusBtn(`cken-${t.therapy_id}`, t.enrollment_envelope_id)}
                    {t.enrollment_lane === 'sent' && t.enrollment_envelope_id && voidBtn(`vden-${t.therapy_id}`, t.enrollment_envelope_id, 'Enrollment')}
                    {docusignOn && t.enrollment_lane === 'sent' && t.enrollment_envelope_id && !isInAppEnvelopeId(t.enrollment_envelope_id) && resendBtn(`ren-${t.therapy_id}`, () => setEnrollFor({
                      key: `ren-${t.therapy_id}`,
                      therapyId: t.therapy_id,
                      providerEmail: t.prescriber_email || '',
                      providerName: t.prescriber_name || '',
                      prescriberId: t.prescriber_id,
                      replaceEnvelopeId: t.enrollment_envelope_id!,
                    }))}
                    {t.enrollment_envelope_id && auditBtn(t.enrollment_envelope_id, 'Enrollment')}
                  </td>
                  <td data-label="Patient consent" style={td}>
                    <Chip s={consentLane}
                      sub={consentLane === 'draft' ? 'waiting on enrollment'
                        : consentLane === 'sent' ? `${t.recipient_email || ''} · ${t.sent_at ? formatUsDate(t.sent_at) : ''}`
                        : consentLane === 'signed' && t.obtained_at ? formatUsDate(t.obtained_at) : null} />
                    {viewBtn(`vco-${t.therapy_id}`, () => viewLane(`vco-${t.therapy_id}`, t, 'consent'),
                      laneLabel(consentLane, t.consent_signed_doc_id, t.envelope_id))}
                    {/* ASP 2026-10-11 (#14): a declined consent can be sent again, but only as a deliberate
                        act — it keeps its red chip and the dialog says the patient declined. */}
                    {/* D-CRX-87 §4d: in_app cannot send a consent (the patient cannot sign in OwlRx yet) — the
                        Send is replaced by a note; the server refuses it too (409 docusign_disabled_for_pharmacy). */}
                    {!docusignOn && (consentLane === 'ready_to_send' || (consentLane === 'declined' && t.consent_status === 'pending' && t.envelope_status === 'declined')) && <DocuSignOffNote />}
                    {docusignOn && (consentLane === 'ready_to_send' || (consentLane === 'declined' && t.consent_status === 'pending' && t.envelope_status === 'declined')) && sendBtn(`co-${t.therapy_id}`, () => setConfirm({
                      key: `co-${t.therapy_id}`,
                      title: consentLane === 'declined' ? 'Send patient consent again — the patient declined the last one' : 'Send patient consent',
                      recipientLabel: `Patient${t.patient_name ? ` — ${t.patient_name}` : ''}`,
                      patientId: t.patient_id,
                      initialEmail: t.patient_email || '',
                      showInterpreter: true,
                      run: (email, interp) => api.sendCareRxConsentEsign(t.therapy_id, { patient_email: email, interpreter_email: interp || undefined }),
                      okMsg: (email) => `Consent sent to ${email}.`,
                    }))}
                    {consentLane === 'sent' && t.envelope_id && statusBtn(`ckco-${t.therapy_id}`, t.envelope_id)}
                    {consentLane === 'sent' && t.envelope_id && voidBtn(`vdco-${t.therapy_id}`, t.envelope_id, 'Consent')}
                    {t.envelope_id && auditBtn(t.envelope_id, 'Consent')}
                    {/* A resend is a NEW envelope: not offered under in_app or on an in-app envelope (Check / Void / Audit stay). */}
                    {docusignOn && consentLane === 'sent' && t.envelope_id && !isInAppEnvelopeId(t.envelope_id) && resendBtn(`re-${t.therapy_id}`, () => setConfirm({
                          // ASP 2026-10-11 (#13): Resend opens the same dialog as Send, so the address can
                          // be corrected before a fresh envelope goes out. Defaults to the same person —
                          // the patient's email on file, else the address the last envelope went to.
                          key: `re-${t.therapy_id}`,
                          title: 'Resend patient consent',
                          recipientLabel: `Patient${t.patient_name ? ` — ${t.patient_name}` : ''}`,
                          patientId: t.patient_id,
                          initialEmail: t.patient_email || t.recipient_email || '',
                          showInterpreter: false,
                          run: (email) => api.resendCareRxConsentEsign(t.envelope_id!, { patient_email: email }),
                          okMsg: (email) => `Consent re-sent to ${email} (fresh envelope).`,
                        }))}
                  </td>
                  <td data-label="Plan of Treatment" style={td}>
                    {/* Owner UI surf 2026-08-13: a claimed visit is not 'no visit yet' — the lane
                        now distinguishes visit_open (visit exists, form not started). */}
                    <Chip s={t.pot_lane === 'visit_open' ? 'not_started' : t.pot_lane}
                      sub={t.pot_lane === 'visit_open' ? `visit ${t.open_visit_status ?? 'open'} · form not started`
                        : t.pot_lane === 'not_started' ? 'no visit yet'
                        : t.pot_lane === 'draft' ? 'nurse documenting'
                        : t.pot_lane === 'sent' ? (t.pot_sent_at ? formatUsDate(t.pot_sent_at) : null)
                        : t.pot_lane === 'signed' && t.pot_signed_at ? formatUsDate(t.pot_signed_at) : null} />
                    {(t.pot_document_id || t.pot_signed_doc_id) && viewBtn(`vpo-${t.therapy_id}`, () => viewLane(`vpo-${t.therapy_id}`, t, 'pot'),
                      t.pot_signed_doc_id ? 'View signed' : 'View POT')}
                    {!docusignOn && t.pot_lane === 'ready_to_send' && t.pot_document_id && <DocuSignOffNote />}
                    {docusignOn && t.pot_lane === 'ready_to_send' && t.pot_document_id && sendBtn(`po-${t.therapy_id}`, () => setConfirm({
                      key: `po-${t.therapy_id}`,
                      title: 'Send Plan of Treatment',
                      recipientLabel: `Physician${t.prescriber_name ? ` — ${t.prescriber_name}` : ''}`,
                      initialEmail: t.prescriber_email || '',
                      run: (email) => api.sendCareRxPotForSignature(t.pot_document_id!, { recipient_email: email, recipient_name: t.prescriber_name || undefined }),
                      okMsg: (email) => `POT sent to ${email} for signature.`,
                    }))}
                    {t.pot_lane === 'sent' && t.pot_envelope_id && statusBtn(`ckpo-${t.therapy_id}`, t.pot_envelope_id)}
                    {t.pot_lane === 'sent' && t.pot_envelope_id && voidBtn(`vdpo-${t.therapy_id}`, t.pot_envelope_id, 'POT')}
                    {docusignOn && t.pot_lane === 'sent' && t.pot_envelope_id && !isInAppEnvelopeId(t.pot_envelope_id) && t.pot_document_id && resendBtn(`rpo-${t.therapy_id}`, () => setConfirm({
                      key: `rpo-${t.therapy_id}`,
                      title: 'Resend Plan of Treatment',
                      recipientLabel: `Physician${t.prescriber_name ? ` — ${t.prescriber_name}` : ''}`,
                      initialEmail: t.prescriber_email || '',
                      run: (email) => api.sendCareRxPotForSignature(t.pot_document_id!, {
                        recipient_email: email, recipient_name: t.prescriber_name || undefined, replace_envelope_id: t.pot_envelope_id!,
                      }),
                      okMsg: (email) => `POT re-sent to ${email} (fresh envelope).`,
                    }))}
                    {t.pot_envelope_id && auditBtn(t.pot_envelope_id, 'POT')}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table></div>
        <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} showPageSize={false} label="plans" />
        </>
      )}
      {confirm && (
        <SendConfirmDialog cfg={confirm} docusignReady={signing.docusignReady} onCancel={() => setConfirm(null)} onSubmit={submitConfirm} />
      )}
      {voidFor && (
        <VoidDialog label={voidFor.label} onCancel={() => setVoidFor(null)} onSubmit={submitVoid} />
      )}
      {enrollFor && (
        <EnrollmentSendDialog cfg={enrollFor} resend={!!enrollFor.replaceEnvelopeId} pharmacyId={activePharmacy?.id}
          mode={signingMode} docusignReady={signing.docusignReady}
          onCancel={() => setEnrollFor(null)} onSubmit={submitEnroll} onSignInApp={submitEnrollInApp} />
      )}
      {signFor && (
        // The tracker reloads when the dialog closes, not on sign: its loading state replaces the whole panel
        // (dialog included), which wiped the "Signed" confirmation the signer had just earned.
        <InAppSignDialog envelopeId={signFor} onClose={() => { setSignFor(null); reload() }} />
      )}
      {auditFor && (
        <AuditTrailDrawer envelopeId={auditFor.envelopeId} label={auditFor.label}
          onNote={setNote} onClose={() => setAuditFor(null)} />
      )}
    </div>
  )
}

/**
 * Void an in-process envelope. A reason is REQUIRED (quick-picks plus free text) and is recorded on
 * the envelope + in the audit trail. Only reachable from an in-process lane; the server refuses to
 * void a completed/signed envelope regardless.
 */
function VoidDialog({ label, onCancel, onSubmit }: {
  label: string; onCancel: () => void; onSubmit: (reason: string) => void
}) {
  const [reason, setReason] = useState('')
  const presets = ['Sent to the wrong recipient', 'Error in the document', 'Recipient requested cancellation', 'Superseded — will resend']
  const valid = reason.trim().length > 0
  return (
    <div style={overlay} onClick={onCancel}>
      <div style={modal} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text)' }}>Void {label} signature</div>
        <div style={{ fontSize: 12.5, color: 'var(--color-text-soft)', marginTop: 4 }}>
          This cancels the in-process envelope at DocuSign. A reason is required and is recorded in the audit trail.
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
          {presets.map(p => (
            <button key={p} type="button" onClick={() => setReason(p)}
              style={{ fontSize: 11.5, padding: '3px 9px', borderRadius: 999, border: '1px solid var(--color-border)',
                       background: reason === p ? 'var(--color-primary)' : 'transparent', color: reason === p ? '#fff' : 'var(--color-text-soft)', cursor: 'pointer' }}>
              {p}</button>
          ))}
        </div>
        <textarea autoFocus value={reason} onChange={e => setReason(e.target.value)} rows={3}
          placeholder="Reason for voiding (required)"
          style={{ width: '100%', boxSizing: 'border-box', marginTop: 10, padding: '8px 10px', borderRadius: 8,
                   border: '1px solid var(--color-border)', fontSize: 13.5, background: 'var(--color-surface)', color: 'var(--color-text)', resize: 'vertical' }} />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onCancel} style={dlgCancel}>Cancel</button>
          <button disabled={!valid} onClick={() => onSubmit(reason.trim())}
            style={{ ...dlgSend, background: '#b91c1c', opacity: valid ? 1 : .5, cursor: valid ? 'pointer' : 'not-allowed' }}>
            Void envelope</button>
        </div>
      </div>
    </div>
  )
}

/**
 * The recipient email is shown and editable BEFORE the envelope is created, replacing the old
 * silent "use the on-file address / only prompt when blank" behaviour. For consent, the edited
 * address can be saved as an additive CareRx contact email (mig 420) so it persists — without
 * writing the PioneerRx-owned dbo.patients.email.
 */
type SendConfirm = {
  key: string
  title: string
  recipientLabel: string
  initialEmail: string
  patientId?: string | null      // set for consent → enables the "save as contact email" option
  showInterpreter?: boolean
  run: (email: string, interpreter?: string) => Promise<unknown>
  okMsg: (email: string) => string
}

// Patient consent and the Plan of Treatment are sent with DocuSign only (owner ruling 2026-10-21, latest): they
// need a patient or provider signature, and neither can sign in OwlRx yet. Under in_app this dialog is never
// opened (the lane shows a note instead). When DocuSign is not set up, Send is disabled and says why.
export function SendConfirmDialog({ cfg, docusignReady = true, onCancel, onSubmit }: {
  cfg: SendConfirm
  docusignReady?: boolean
  onCancel: () => void
  onSubmit: (email: string, interpreter: string, persist: boolean) => void
}) {
  const usePicker = !!cfg.patientId
  const [email, setEmail] = useState(cfg.initialEmail)   // plain path (POT: no subject to pick from)
  const [choice, setChoice] = useState<EmailChoice>({ email: cfg.initialEmail, saveToProfile: false })
  const [interpreter, setInterpreter] = useState('')
  const effectiveEmail = usePicker ? choice.email : email
  // The interpreter address is optional, but a malformed one is refused by the server after Send.
  const interpOk = !interpreter.trim() || isEmail(interpreter)
  const valid = isEmail(effectiveEmail) && interpOk && docusignReady
  const fieldLbl: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--color-text-soft)', marginTop: 12 }
  const fieldInp: React.CSSProperties = { width: '100%', boxSizing: 'border-box', marginTop: 4, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--color-border)', fontSize: 13.5, background: 'var(--color-surface)', color: 'var(--color-text)' }
  return (
    <div style={overlay} onClick={onCancel}>
      <div style={modal} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text)' }}>{cfg.title}</div>
        <div style={{ fontSize: 12.5, color: 'var(--color-text-soft)', marginTop: 4 }}>
          Review the recipient before this is sent for signature.
        </div>
        <div style={fieldLbl}>{cfg.recipientLabel} — email</div>
        {usePicker
          ? <EmailPicker subjectType="patient" subjectId={cfg.patientId!} roleLabel="patient"
              fallbackEmail={cfg.initialEmail} onChange={setChoice} />
          : <input autoFocus type="email" value={email} onChange={e => setEmail(e.target.value)}
              style={fieldInp} placeholder="name@example.com" />}
        {cfg.showInterpreter && (
          // A sibling label, not a wrapping one: a click anywhere in a wrapping <label> focuses its
          // input, which pulled focus off the recipient email above (ASP 2026-10-14 #6).
          <>
            <label htmlFor="consent-interpreter-email" style={fieldLbl}>Interpreter email — Section J (optional)</label>
            <input id="consent-interpreter-email" type="email" value={interpreter} onChange={e => setInterpreter(e.target.value)}
              style={fieldInp} placeholder="leave blank if not needed" />
            {!interpOk && <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 4 }}>Enter a full email address, or leave it blank.</div>}
          </>
        )}
        {!docusignReady && <DocuSignNotSetUpNote />}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
          <button onClick={onCancel} style={dlgCancel}>Cancel</button>
          <button disabled={!valid} title={docusignReady ? undefined : SIGNING_WORDS.docusignNotSetUp} onClick={() => onSubmit(effectiveEmail.trim(), interpreter.trim(), usePicker ? choice.saveToProfile : false)}
            style={{ ...dlgSend, opacity: valid ? 1 : .5, cursor: valid ? 'pointer' : 'not-allowed' }}>
            <Send size={13} /> Send
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Enrollment send: the form goes to a provider AND a pharmacist. This lets the operator pick which
 * pharmacist signs (from the pharmacy's staff) and the signing ORDER — pharmacist first by default,
 * with the provider first one click away. Recipients are the two the enrollment document has signature
 * blocks for; nothing arbitrary (the PDF has no block for anyone else).
 */
export function EnrollmentSendDialog({ cfg, resend = false, pharmacyId, mode, docusignReady = true, onCancel, onSubmit, onSignInApp }: {
  cfg: { providerEmail: string; providerName: string; prescriberId: string | null }
  /** Resend: the open envelope is voided and replaced once this send goes out. */
  resend?: boolean
  pharmacyId?: string
  mode: SigningMode
  docusignReady?: boolean
  onCancel: () => void
  onSubmit: (providerEmail: string, pharmacistEmail: string, pharmacistName: string, providerFirst: boolean, providerSave: boolean) => void
  onSignInApp: (pharmacistAccountId: string, providerFirst: boolean) => void
}) {
  // D-CRX-87 §4d: docusign = today; both = "Send with DocuSign" or "Sign in app" (the pharmacist signs in their
  // own OwlRx session; the provider signs on their OwlRx provider portal when they have a login — D-POT-8);
  // in_app = "Sign in app" only (no DocuSign send — the server refuses it too). "Send with DocuSign" is disabled,
  // with words, when DocuSign is not set up for the pharmacy.
  // A resend replaces a DocuSign envelope with a new DocuSign one (go-live #11): no in-app option there.
  const inApp = allowsInApp(mode) && !resend
  const docuSign = allowsDocuSign(mode)
  const [providerChoice, setProviderChoice] = useState<EmailChoice>({ email: cfg.providerEmail, saveToProfile: false })
  const [users, setUsers] = useState<api.PharmacyUser[]>([])
  const [pharmacistId, setPharmacistId] = useState('')
  // Default: pharmacist signs FIRST, then the provider (ASP ticket 2026-09-30; the D-CRX-59b
  // default, and the server's when provider_first is absent). The dialog can still flip it per send.
  const [providerFirst, setProviderFirst] = useState(false)
  const [loadErr, setLoadErr] = useState(false)
  useEffect(() => {
    if (!pharmacyId) { setLoadErr(true); return }
    api.listPharmacyUsers(pharmacyId)
      .then(d => setUsers((d.users || []).filter(u => u.status === 'active')))
      .catch(() => setLoadErr(true))
  }, [pharmacyId])
  const pharmacist = users.find(u => u.id === pharmacistId)
  const validProvider = isEmail(providerChoice.email)
  const canSend = docuSign && docusignReady && validProvider && !!pharmacist
  const provLabel = `Provider${cfg.providerName ? ` (${cfg.providerName})` : ''}`
  const pharmLabel = `Pharmacist${pharmacist ? ` (${pharmacist.display_name})` : ''}`
  const fieldLbl: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--color-text-soft)', marginTop: 12 }
  return (
    <div style={overlay} onClick={onCancel}>
      <div style={modal} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text)' }}>{resend ? 'Resend enrollment form' : 'Send enrollment form'}</div>
        <div style={{ fontSize: 12.5, color: 'var(--color-text-soft)', marginTop: 4 }}>
          The enrollment is signed by the provider and a pharmacist. Choose both and the signing order.
          {resend && ' The envelope that is out now is voided when this one is sent.'}
        </div>
        {docuSign && <>
          <div style={fieldLbl}>Provider — email{inApp ? ' (Send with DocuSign only)' : ''}</div>
          <EmailPicker subjectType="prescriber" subjectId={cfg.prescriberId} roleLabel="provider"
            fallbackEmail={cfg.providerEmail} onChange={setProviderChoice} />
        </>}
        {inApp && docuSign && <div data-testid="sign-in-app-note" style={{ fontSize: 12, color: 'var(--color-text-soft)', marginTop: 10 }}>
          Sign in app: the pharmacist signs in their own OwlRx session and the provider signs on their OwlRx provider portal — nothing is emailed. A provider with no OwlRx login cannot sign in app; use Send with DocuSign for them.
        </div>}
        {!docuSign && <Box data-testid="in-app-only-note" sx={{ mt: 1.5 }}><Notice severity="info">{SIGNING_WORDS.enrollmentInAppOnly}</Notice></Box>}
        <div style={fieldLbl}>Pharmacist — who signs</div>
        {/* Typeahead rather than a plain dropdown: the staff list grows with the pharmacy (Gate 19). */}
        <SearchableSelect value={pharmacistId} onChange={setPharmacistId}
          options={users.map(u => ({ value: u.id, label: u.display_name, hint: u.email }))}
          ariaLabel="Pharmacist who signs" placeholder="— choose a pharmacist —"
          emptyText={loadErr ? 'Staff list unavailable' : 'No active pharmacists'}
          style={{ marginTop: 4 }} />
        {loadErr && <div style={{ fontSize: 12, color: '#b45309', marginTop: 6 }}>Couldn&rsquo;t load the staff list — reopen the dialog to retry.</div>}
        <div style={fieldLbl}>Signing order</div>
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          {[{ v: false, l: 'Pharmacist first, then provider' }, { v: true, l: 'Provider first, then pharmacist' }].map(o => (
            <button key={String(o.v)} type="button" aria-pressed={providerFirst === o.v} onClick={() => setProviderFirst(o.v)}
              style={{ flex: 1, fontSize: 12, padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                       border: `1px solid ${providerFirst === o.v ? 'var(--color-primary)' : 'var(--color-border)'}`,
                       background: providerFirst === o.v ? 'var(--color-primary)' : 'transparent',
                       color: providerFirst === o.v ? '#fff' : 'var(--color-text-soft)' }}>{o.l}</button>
          ))}
        </div>
        <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginTop: 10 }}>
          Order: <b>1.</b> {providerFirst ? provLabel : pharmLabel} &nbsp;→&nbsp; <b>2.</b> {providerFirst ? pharmLabel : provLabel}
        </div>
        {docuSign && !docusignReady && <DocuSignNotSetUpNote />}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
          <button onClick={onCancel} style={dlgCancel}>Cancel</button>
          {inApp && (
            <button disabled={!pharmacist} data-testid="sign-in-app" onClick={() => onSignInApp(pharmacist!.id, providerFirst)}
              style={{ ...dlgSend, background: 'var(--color-surface)', color: 'var(--color-primary)',
                       border: '1px solid var(--color-primary)', opacity: pharmacist ? 1 : .5, cursor: pharmacist ? 'pointer' : 'not-allowed' }}>
              Sign in app
            </button>
          )}
          {docuSign && (
            <button disabled={!canSend} title={docusignReady ? undefined : SIGNING_WORDS.docusignNotSetUp}
              onClick={() => onSubmit(providerChoice.email.trim(), pharmacist!.email, pharmacist!.display_name, providerFirst, providerChoice.saveToProfile)}
              style={{ ...dlgSend, opacity: canSend ? 1 : .5, cursor: canSend ? 'pointer' : 'not-allowed' }}>
              <Send size={13} /> {inApp ? 'Send with DocuSign' : 'Send'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// D-CRX-81 — the per-envelope audit trail. A drawer opened from the "Audit" button next to a
// document: one time-ordered timeline that merges OUR audit_log actions (send/resend/refresh/void/
// fetch) with the LIVE DocuSign recipient events (sent / viewed / signed / declined, per recipient),
// plus a one-click download of the tamper-evident Certificate of Completion.
// 'record' = the in-app envelope's tamper-evident signing record (carerx_esign_events, hash-chained, printed on the
// certificate). It is a different write from the OwlRx audit log ('owl'): one in-app signature leaves one row in each,
// and both are kept (never drop an audit write) — the labels and the tag say which is which.
type TimelineItem = { time: string | null; who: string; what: string; detail?: string | null; source: 'owl' | 'docusign' | 'record'; code?: string }

const OWL_ACTION_LABEL: Record<string, string> = {
  'carerx.consent.esign_send': 'Consent sent for signature',
  'carerx.enrollment.esign_send': 'Enrollment sent for signature',
  'carerx.consent.esign_resend': 'Re-sent for signature',
  'carerx.esign.refresh': 'Checked status at DocuSign',
  'carerx.esign.void': 'Voided the envelope',
  'carerx.esign.remind': 'Reminder sent to the signer',
  'carerx.esign.webhook': 'Status update received',
  'carerx.esign.fetch_signed': 'Filed the signed document',
  'carerx.esign.fetch_certificate': 'Filed the Certificate of Completion',
  // Written against the live consent envelope when a visit is staffed (CareRxStaffingFunctions):
  // the nurse is added to the open envelope as the Section I attestation signer.
  'carerx.consent.nurse_recipient_added': 'Nurse added as a signer',
  'carerx.consent.nurse_recipient_skipped': 'Nurse signer skipped',
  // D-ESIGN-1 in-app signing
  'carerx.esign.inapp_created': 'Opened for signing in OwlRx',
  'carerx.esign.inapp_signed': 'Signed in OwlRx',
  'carerx.esign.inapp_reauth_failed': 'Signing re-authentication failed',
  'carerx.esign.inapp_completed': 'All signed — signed copy and certificate filed',
  'carerx.esign.inapp_verified': 'Integrity verified',
}
const INAPP_EVENT_LABEL: Record<string, string> = {
  created: 'Document sealed for signing', viewed: 'Document viewed', signed: 'Signature sealed in the signing record',
  reauth_failed: 'Re-authentication failure recorded', completed: 'Signing record completed', voided: 'Void recorded',
}
const AUDIT_SOURCE_TAG: Record<TimelineItem['source'], string> = { owl: 'OwlRx', docusign: 'DocuSign', record: 'Signing record' }

/**
 * A raw action code must never reach a pharmacist (D-CRX-29 T7 — Andrew's walk; a raw server string
 * is also an information-disclosure path, Sasha). Anything not in the map above degrades to a
 * neutral phrase, and the code goes to the title attribute for support rather than the row text.
 */
function owlActionLabel(action: string): string {
  return OWL_ACTION_LABEL[action] ?? 'Recorded action'
}
function AuditTrailDrawer({ envelopeId, label, onNote, onClose }: {
  envelopeId: string; label: string; onNote: (n: string | null) => void; onClose: () => void
}) {
  const [data, setData] = useState<api.EsignEnvelopeAudit | null>(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [cert, setCert] = useState(false)

  useEffect(() => {
    let live = true
    setLoading(true); setErr(null)
    api.getCareRxEsignAudit(envelopeId)
      .then(d => { if (live) setData(d) })
      .catch(e => { if (live) setErr(friendlyError(e)) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [envelopeId])

  // Flatten our actions + each DocuSign recipient's discrete events into one sorted timeline.
  const timeline: TimelineItem[] = []
  if (data) {
    for (const a of data.our_actions)
      timeline.push({ time: a.at, who: a.actor || 'System', what: owlActionLabel(a.action), source: 'owl', code: a.action })
    for (const r of data.docusign_events) {
      const who = r.name || r.email || 'Recipient'
      if (r.sent_at) timeline.push({ time: r.sent_at, who, what: 'Sent', detail: r.email, source: 'docusign' })
      if (r.delivered_at) timeline.push({ time: r.delivered_at, who, what: 'Viewed', detail: r.email, source: 'docusign' })
      if (r.completed_at && (r.status === 'completed' || r.status === 'signed'))
        timeline.push({ time: r.completed_at, who, what: 'Signed', detail: r.email, source: 'docusign' })
      if (r.declined_at) timeline.push({ time: r.declined_at, who, what: 'Declined', detail: r.declined_reason || r.email, source: 'docusign' })
    }
    for (const ev of data.inapp_events ?? [])
      timeline.push({ time: ev.at, who: ev.actor || 'OwlRx', what: INAPP_EVENT_LABEL[ev.event_type] ?? 'Recorded event', detail: null, source: 'record', code: ev.event_type })
    timeline.sort((a, b) => (a.time || '').localeCompare(b.time || ''))
  }

  const terminal = data?.status != null && ['signed', 'completed', 'voided', 'declined'].includes(data.status)
  const downloadCert = async () => {
    setCert(true); onNote(null)
    try {
      const doc = data?.certificate_document_id
        ? { document_id: data.certificate_document_id }
        : await api.fetchCareRxEsignCertificate(envelopeId)
      await api.openCareRxPdfDocument(doc.document_id)
    } catch (e) {
      onNote(esignErrorText(e))
    } finally { setCert(false) }
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...modal, maxWidth: 560, maxHeight: '82vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <History size={16} style={{ color: 'var(--color-primary)' }} />
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text)' }}>{label} — audit trail</div>
        </div>
        <div style={{ fontSize: 12, color: 'var(--color-text-mute)', marginTop: 2, fontFamily: 'var(--font-mono, monospace)' }}>{envelopeId}</div>

        <div style={{ flex: 1, overflowY: 'auto', marginTop: 14 }}>
          {loading && <div style={{ fontSize: 13, color: 'var(--color-text-soft)' }}><Loader2 className="spin" size={13} /> Loading the trail…</div>}
          {err && <div style={{ fontSize: 13, color: '#b91c1c' }}>{err}</div>}
          {!loading && !err && timeline.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--color-text-soft)' }}>No recorded activity yet.</div>
          )}
          {!loading && !err && timeline.map((it, i) => (
            <div key={i} style={{ display: 'flex', gap: 10, padding: '9px 0', borderTop: i === 0 ? 'none' : '1px solid var(--color-border)' }}>
              <span style={{ marginTop: 3, width: 8, height: 8, borderRadius: 999, flexShrink: 0,
                background: it.source === 'docusign' ? '#0369a1' : 'var(--color-text-mute)' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: 'var(--color-text)' }}>
                  <strong title={it.code}>{it.what}</strong>
                  <span style={{ fontSize: 10.5, fontWeight: 700, marginLeft: 8, padding: '1px 6px', borderRadius: 999,
                    color: it.source === 'docusign' ? '#0369a1' : 'var(--color-text-soft)',
                    background: it.source === 'docusign' ? 'rgba(3,105,161,.10)' : 'var(--color-bg)' }}>
                    {AUDIT_SOURCE_TAG[it.source]}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-text-soft)', marginTop: 1 }}>
                  {it.who}{it.detail ? ` · ${it.detail}` : ''}
                </div>
              </div>
              <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', whiteSpace: 'nowrap', marginTop: 1 }}>
                {it.time ? new Date(it.time).toLocaleString() : '—'}
              </div>
            </div>
          ))}
          {!loading && !err && data?.docusign_source === 'unavailable' && (
            <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', marginTop: 10, fontStyle: 'italic' }}>
              Live DocuSign events could not be loaded — showing OwlRx actions only.
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
          <button disabled={!terminal || cert} onClick={downloadCert} title={terminal ? 'Download the tamper-evident Certificate of Completion' : 'Available once the envelope is signed, voided or declined'}
            style={{ ...dlgCancel, opacity: terminal ? 1 : .5, cursor: terminal ? 'pointer' : 'not-allowed', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {cert ? <Loader2 className="spin" size={13} /> : <FileText size={13} />} Certificate of Completion
          </button>
          <button onClick={onClose} style={dlgSend}>Close</button>
        </div>
      </div>
    </div>
  )
}

const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }
const modal: React.CSSProperties = { width: '100%', maxWidth: 440, background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 20, boxShadow: '0 20px 50px rgba(15,23,42,.25)' }
const dlgCancel: React.CSSProperties = { padding: '8px 14px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-surface)', color: 'var(--color-text-soft)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }
const dlgSend: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: 'none', background: 'var(--color-primary)', color: '#fff', fontWeight: 700, fontSize: 13 }

/**
 * Module 6 — patient consent-to-treat + fill/shipment. Consent is captured per type as attestation
 * (verbal/written/e-sign), with revocation; fills move pending → confirmed → shipped → delivered.
 */
export default function CareRxConsentFulfillment() {
  const { activePharmacy } = usePharmacy()
  const [patient, setPatient] = useState<PickedEntity | null>(null)
  const [crows, setCrows] = useState<CRow[]>([])
  // ASP-CRX-14: "Mark signed" asks for the scan reference of the ink-signed consent (row index + typed ref)
  const [scanFor, setScanFor] = useState<number | null>(null)
  const [scanRef, setScanRef] = useState('')
  const [fills, setFills] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [fillForm, setFillForm] = useState({ drug: '', quantity: '', lot: '', ndc: '' })
  const [shipOpen, setShipOpen] = useState<string | null>(null)
  const [lane, setLane] = useState<any[]>([])
  useEffect(() => { api.getCareRxDispenseLane().then(d => setLane(d.items ?? [])).catch(() => { /* pre-241 tenant */ }) }, [])
  const [ship, setShip] = useState({ carrier: '', tracking_number: '' })
  const [packetChoices, setPacketChoices] = useState<api.CareRxTherapyLane[] | null>(null)
  const [wlLoading, setWlLoading] = useState(true)
  const [awaiting, setAwaiting] = useState<api.CareRxAwaitingRow[]>([])
  // Paginate the cross-patient worklists (can grow on a busy pharmacy).
  const awaitingFx = useTableFunnels(awaiting, AWAITING_FUNNELS)
  const fillFx = useTableFunnels(fills, FILL_FUNNELS)
  const awaitingPager = usePaged(awaitingFx.rows, DEFAULT_PAGE_SIZE, awaitingFx.sig)
  const lanePager = usePaged(lane, DEFAULT_PAGE_SIZE)
  const [nudging, setNudging] = useState<string | null>(null)
  // Go-live #3: an envelope still out for signature on a DELETED enrollment no longer has a tracker
  // row (the tracker hides cancelled therapies), so this queue — every live envelope — is where it
  // can still be voided by hand when the delete could not void it at DocuSign.
  const [voidAwaiting, setVoidAwaiting] = useState<api.CareRxAwaitingRow | null>(null)
  const reloadAwaiting = useCallback(() => { api.listCareRxEsignAwaiting().then(d => setAwaiting(d.items ?? [])).catch(() => setAwaiting([])) }, [])

  const load = useCallback(async (p: PickedEntity) => {
    setLoading(true); setMsg(null)
    try {
      const c = await api.listCareRxConsents(p.id)
      const byType = new Map<string, any>((c.items || []).map((i: any) => [i.consent_type, i]))
      setCrows(CONSENT_TYPES.map(t => {
        const ex = byType.get(t.key)
        return { type: t.key, label: t.label, id: ex?.id, status: ex?.status || 'pending', method: ex?.method || '', expires_at: ex?.expires_at ? String(ex.expires_at).slice(0, 10) : '', notes: ex?.notes || '' }
      }))
      const f = await api.listCareRxFills(p.id)
      setFills(f.items || [])
    } catch (e: any) { setMsg(friendlyError(e)) } finally { setLoading(false) }
  }, [])

  const onPick = (p: PickedEntity | null) => {
    setPatient(p); setCrows([]); setFills([]); setMsg(null); setShipOpen(null); setPacketChoices(null)
    setFillForm({ drug: '', quantity: '', lot: '', ndc: '' })
    if (p) load(p)
  }

  // The cross-patient consent/fill worklists this used to feed are gone (see the note by the
  // !patient block); wlLoading now only gates the MD-signature queue's first paint.
  useEffect(() => { setWlLoading(false) }, [])
  useEffect(() => { reloadAwaiting() }, [reloadAwaiting])

  const setCRow = (i: number, patch: Partial<CRow>) => setCrows(rs => rs.map((r, j) => j === i ? { ...r, ...patch } : r))

  const saveConsents = async () => {
    if (!patient) return
    setBusy(true); setMsg(null)
    try {
      await api.upsertCareRxConsents(patient.id, crows.map(r => ({ consent_type: r.type, status: r.status, method: r.method || undefined, expires_at: r.expires_at || undefined, notes: r.notes || undefined })))
      setMsg('Consents saved.'); await load(patient)
    } catch (e: any) { setMsg(friendlyError(e)) } finally { setBusy(false) }
  }
  /**
   * PRINT MODE (ASP-CRX-14 / spec Stage 5). No e-consent -> the packet prints, and the pending rows
   * park as awaiting_wet_signature (chased in the worklist) until marked signed WITH the scan
   * reference.
   *
   * WHAT prints is the GENERATED Patient Consent for Services for this patient's therapy — the same
   * document the e-sign lane sends and every other CareRx screen opens. It used to be an HTML page
   * this component wrote by hand: a heading, one invented sentence per consent type ("I consent as
   * described for: HIPAA authorization."), and a signature rule. That page carried no pharmacy
   * identity, no jurisdiction clauses and no legal wording of any kind, and staff were then asked to
   * file the signature against it as the patient's consent of record. A fabricated consent is worse
   * than a missing one, so the hand-rolled packet is gone.
   *
   * The consent is THERAPY-grain, so resolve the therapy first (one -> print; several -> ask; none
   * -> say so and change nothing, rather than parking rows against a document that never printed).
   */
  const parkPending = () => crows.forEach((r, idx) => {
    // paper replaces an e-consent that was never sent: a ready_to_send row is parked too
    if (unsigned(r.status)) setCRow(idx, { status: 'awaiting_wet_signature', method: r.method === 'esign' || !r.method ? 'written' : r.method })
  })
  const printPacketFor = async (therapyId: string, tab?: Window | null) => {
    setPacketChoices(null); setBusy(true); setMsg(null)
    try {
      await openTherapyDocument('consent', therapyId, activePharmacy ?? undefined, tab)
      // Park the rows ONLY once the consent actually opened. Parking them against a document that
      // never rendered is how a patient ends up chased in the worklist for a signature on nothing.
      parkPending()
      setMsg('Consent opened for printing — pending rows are now awaiting a physical signature. Save consents to record that.')
    } catch (e: any) { tab?.close(); setMsg(friendlyError(e)) } finally { setBusy(false) }
  }
  const printPacket = async () => {
    if (!patient) return
    setBusy(true); setMsg(null); setPacketChoices(null)
    const tab = claimDocumentTab('consent')   // claimed in the gesture, before the therapy lookup
    try {
      const r = await resolveTherapyForDocument(patient.id, 'consent')
      if ('therapyId' in r) { setBusy(false); await printPacketFor(r.therapyId, tab); return }
      tab?.close(); setPacketChoices(r.choices); setBusy(false)
    } catch (e: any) { tab?.close(); setMsg(friendlyError(e)); setBusy(false) }
  }

  const revokeConsent = async (id: string) => {
    if (!patient) return
    setBusy(true); setMsg(null)
    try { await api.updateCareRxConsent(id, { action: 'revoke' }); setMsg('Consent revoked.'); await load(patient) }
    catch (e: any) { setMsg(friendlyError(e)) } finally { setBusy(false) }
  }

  const addFill = async () => {
    if (!patient || !fillForm.drug.trim()) { setMsg('Enter a drug for the fill.'); return }
    setBusy(true); setMsg(null)
    try { await api.createCareRxFill(patient.id, { ...fillForm }); setFillForm({ drug: '', quantity: '', lot: '', ndc: '' }); setMsg('Fill created.'); await load(patient) }
    catch (e: any) { setMsg(friendlyError(e)) } finally { setBusy(false) }
  }
  const fillAction = async (fillId: string, action: 'confirm' | 'ship' | 'deliver' | 'cancel', extra?: any) => {
    if (!patient) return
    setBusy(true); setMsg(null)
    try { await api.updateCareRxFill(fillId, { action, ...extra }); setShipOpen(null); setShip({ carrier: '', tracking_number: '' }); setMsg('Fill updated.'); await load(patient) }
    catch (e: any) { setMsg(friendlyError(e)) } finally { setBusy(false) }
  }

  const cStatusColor = (s: string) => s === 'obtained' ? ['#15803d', 'rgba(22,163,74,.12)'] : s === 'declined' || s === 'revoked' ? ['#b91c1c', 'rgba(220,38,38,.10)'] : ['#b45309', 'rgba(217,119,6,.12)']
  const fStatusColor = (s: string) => s === 'delivered' ? ['#15803d', 'rgba(22,163,74,.12)'] : s === 'shipped' ? ['#0369a1', 'rgba(3,105,161,.12)'] : s === 'cancelled' ? ['#b91c1c', 'rgba(220,38,38,.10)'] : s === 'confirmed' ? ['#334155', 'var(--color-bg)'] : ['#b45309', 'rgba(217,119,6,.12)']

  return (
    <div>
      {/* D-CRX-59: the document tracker leads the tab — every Plan of Treatment's three documents
          (enrollment / consent / POT) with drafts visible from birth and phase-gated Send. */}
      {!patient && <DocTrackerPanel onPickPatient={onPick} />}
      {/* ASP-CRX-43 / Master 8b (D2, specialty-only): the dispense-only lane — the volume track on
          its three verbs: SHIP on PAID, REFILL due, OUTREACH. Excluded_not_carerx never appears. */}
      {lane.length > 0 && (
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 10, padding: 12, marginBottom: 14 }}>
          <b style={{ fontSize: 15 }}>Dispense-only lane</b>
          <Pager page={lanePager.page} totalPages={lanePager.totalPages} total={lanePager.total} pageSize={lanePager.pageSize} onPage={lanePager.setPage} onPageSize={lanePager.setPageSize} label="therapies" />
          {lanePager.pageRows.map(l0 => (
            <div key={l0.therapy_id} style={{ borderTop: '1px solid var(--color-border)', padding: '6px 0', fontSize: 12.5, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <span><b>{l0.patient_name}</b><PatientLanguageBadge patientId={l0.patient_id} /> · {l0.drug_name || 'therapy'}</span>
              {l0.ship_ready && <span style={{ fontWeight: 700, color: '#15803d' }}>PAID — ready to ship</span>}
              {l0.claim_status && l0.claim_status !== 'paid' && <span style={{ color: '#b45309' }}>claim {l0.claim_status}</span>}
              {l0.fill_status && <span style={{ color: 'var(--color-text-mute)' }}>{l0.fill_status}</span>}
              {l0.renewal_due && <span style={{ color: l0.renewal_overdue ? '#b91c1c' : 'var(--color-text-mute)' }}>refill due {formatUsDate(l0.renewal_due)}</span>}
              {l0.last_outreach && <span style={{ color: 'var(--color-text-mute)' }}>outreach {formatUsDate(l0.last_outreach)}</span>}
            </div>
          ))}
          <Pager page={lanePager.page} totalPages={lanePager.totalPages} total={lanePager.total} pageSize={lanePager.pageSize} onPage={lanePager.setPage} showPageSize={false} label="therapies" />
        </div>
      )}
      {loading && <div style={muted}><Loader2 className="spin" size={15} /> Loading…</div>}
      {msg && <div style={{ fontSize: 12, color: /saved|created|updated|revoked|Reminder sent|voided/.test(msg) ? 'var(--color-primary)' : '#dc2626', marginBottom: 10 }}>{msg}</div>}

      {/* D-CRX-73 review (Mario 2026-09-02). This tab used to open with THREE ways to find a
          patient: an "Open consents" list, a "Fills in flight" list, and a patient search box under
          them — while the document tracker at the top already had a row per Plan of Treatment. Both
          lists restated the tracker one consent-type row at a time, and on a pharmacy with nothing
          outstanding they were two empty cards and a sentence, which was most of what rendered.
          All three are gone. ONE search, on the tracker itself, and the patient's name in each row
          opens their consents and fills — the same destination the lists and the picker led to.
          "Awaiting MD signature" stays: a different queue (D-CRX-34 G2b, Andrew's screen) over
          carerx_esign_envelopes, not a restatement of the tracker. */}
      {!patient && (
        wlLoading ? <div style={muted}><Loader2 className="spin" size={15} /> Loading worklist…</div> : (
          <div style={{ display: 'grid', gap: 18, maxWidth: '100%' }}>
            {awaiting.length > 0 && (
              <div>
                {/* D-CRX-34 G2b: Awaiting MD Signature queue — Andrew's screen, over carerx_esign_envelopes.
                    Oldest-first; rows aging past 3 days flagged; Nudge has DocuSign email the pending signer again. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, marginBottom: 8 }}><FileCheck2 size={15} color="#7c3aed" /> Awaiting MD signature ({awaiting.length})</div>
                <><Pager page={awaitingPager.page} totalPages={awaitingPager.totalPages} total={awaitingPager.total} pageSize={awaitingPager.pageSize} onPage={awaitingPager.setPage} onPageSize={awaitingPager.setPageSize} label="awaiting signature" />
                {awaitingFx.chips}
                <div style={{ overflowX: 'auto' }}><table {...awaitingFx.tableProps} className="rwd-stack" style={{ width: '100%', minWidth: 'max-content', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead><tr>{['Recipient', 'Subject', 'Status', 'Sent', ''].map(h => <th key={h} style={th}>{h} {awaitingFx.funnel(h)}</th>)}</tr></thead>
                  <tbody>
                    {awaitingPager.pageRows.map(a => (
                      <tr key={a.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                        <td data-label="Recipient" style={td}>{a.recipient_name || a.recipient_email || '—'}</td>
                        <td data-label="Subject" style={td}>{a.subject_type}</td>
                        <td data-label="Status" style={td}>
                          <span style={{ color: a.aging ? '#b91c1c' : 'var(--color-text-soft)' }}>
                            {a.status}{a.aging ? ` · aging (${a.days_out}d)` : ''}
                          </span>
                        </td>
                        <td data-label="Sent" style={td}>{a.sent_at ? formatUsDate(a.sent_at) : (a.created_at ? formatUsDate(a.created_at) : '—')}</td>
                        <td style={td}>
                          {a.envelope_id && !isInAppEnvelopeId(a.envelope_id) ? (
                            <button disabled={nudging === a.id}
                              style={{ fontSize: 12, border: '1px solid var(--color-border)', borderRadius: 6, padding: '3px 10px', background: 'transparent', cursor: 'pointer' }}
                              onClick={async () => {
                                setNudging(a.id)
                                try { await api.remindCareRxEsign(a.envelope_id); setMsg('Reminder sent to the signer.') }
                                catch (e: any) { setMsg(friendlyError(e)) } finally { setNudging(null) }
                              }}>{nudging === a.id ? 'Sending…' : 'Nudge'}</button>
                          ) : null}
                          {a.envelope_id && (
                            <button disabled={nudging === a.id} title="Void this in-process signature (reason required)"
                              style={{ fontSize: 12, marginLeft: 6, border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 6, padding: '3px 10px', background: 'transparent', cursor: 'pointer' }}
                              onClick={() => setVoidAwaiting(a)}>Void</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
                <Pager page={awaitingPager.page} totalPages={awaitingPager.totalPages} total={awaitingPager.total} pageSize={awaitingPager.pageSize} onPage={awaitingPager.setPage} showPageSize={false} label="awaiting signature" /></>
                {voidAwaiting && (
                  <VoidDialog label={voidAwaiting.subject_type} onCancel={() => setVoidAwaiting(null)}
                    onSubmit={async reason => {
                      const a = voidAwaiting
                      setVoidAwaiting(null); setNudging(a.id)
                      try { await api.voidCareRxEsign(a.envelope_id, reason); setMsg(`Envelope voided (${a.subject_type}).`); reloadAwaiting() }
                      catch (e) {
                        const b = (e as { body?: { message?: string } }).body
                        setMsg(b?.message ?? friendlyError(e))
                      } finally { setNudging(null) }
                    }} />
                )}
              </div>
            )}
          </div>
        )
      )}

      {patient && !loading && (
        <div style={{ display: 'grid', gap: 14, maxWidth: '100%' }}>
          <button onClick={() => onPick(null)} style={{ background: 'none', border: 'none', color: 'var(--color-primary)', cursor: 'pointer', fontSize: 13, padding: 0, textAlign: 'left', justifySelf: 'start' }}>← Back to all Plans of Treatment</button>
          {/* Consent-to-treat */}
          <div style={card}>
            <div style={cardTitle}><FileCheck2 size={14} /> Consent to treat</div>
            <div style={{ display: 'grid', gap: 8 }}>
              {crows.map((r, i) => {
                const [fg, bg] = cStatusColor(r.status)
                return (
                  <div key={r.type} style={{ display: 'grid', gridTemplateColumns: '1.3fr 1fr 1fr 1fr auto', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 13 }}>{r.label}</span>
                    <select style={input} value={r.status} onChange={e => setCRow(i, { status: e.target.value })}>
                      {(C_STATUS.includes(r.status) ? C_STATUS : [r.status, ...C_STATUS]).map(o => <option key={o} value={o}>{(o.charAt(0).toUpperCase() + o.slice(1)).replace(/_/g, ' ')}</option>)}
                    </select>
                    <select style={input} value={r.method} onChange={e => setCRow(i, { method: e.target.value })}>
                      {C_METHOD.map(o => <option key={o} value={o}>{o ? o.charAt(0).toUpperCase() + o.slice(1) : 'method'}</option>)}
                    </select>
                    <DateInput label="Consent expires on" style={input} value={r.expires_at} onChange={v => setCRow(i, { expires_at: v ?? '' })} title="expires" />
                    {r.id && r.status === 'obtained'
                      ? <button disabled={busy} style={linkDanger} onClick={() => revokeConsent(r.id!)}>Revoke</button>
                      : r.status === 'awaiting_wet_signature'
                        ? <button disabled={busy} style={{ fontSize: 11.5, border: '1px solid var(--color-primary)', color: 'var(--color-primary)', background: 'transparent', borderRadius: 7, padding: '3px 8px', cursor: 'pointer' }}
                            onClick={() => { setScanFor(i); setScanRef('') }}>Mark signed</button>
                        : <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'capitalize', padding: '3px 8px', borderRadius: 999, color: fg, background: bg, justifySelf: 'start' }}>{r.status.replace(/_/g, ' ')}</span>}
                  </div>
                )
              })}
            </div>
            {/* ASP-CRX-14: marking a printed consent signed requires the scan reference — the server refuses
                the transition without a [scan:] marker (named 422). */}
            <AppDialog open={scanFor !== null} title="Physical signature received" onClose={() => setScanFor(null)} maxWidth="xs"
              actions={<>
                <AppButton onClick={() => setScanFor(null)}>Cancel</AppButton>
                <AppButton variant="contained" disabled={!scanRef.trim()} onClick={() => {
                  const i = scanFor; if (i === null) return
                  const r = crows[i]
                  setCRow(i, { status: 'obtained', notes: `${r.notes ? r.notes + ' ' : ''}[scan: ${scanRef.trim()}]` })
                  setScanFor(null)
                }}>Mark signed</AppButton>
              </>}>
              <AppTextField fullWidth size="small" label="Scan / document reference" required value={scanRef}
                onChange={e => setScanRef(e.target.value)} helperText={scanFor !== null ? crows[scanFor]?.label : undefined} />
            </AppDialog>
            {crows.some(r => unsigned(r.status)) && (
              <button disabled={busy} style={{ fontSize: 12.5, border: '1px solid var(--color-border)', background: 'transparent', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', marginTop: 10, marginRight: 8 }}
                title="Open the generated Patient Consent for Services for printing, and park the pending rows as awaiting a physical signature"
                onClick={printPacket}>No e-consent — print packet</button>
            )}
            {packetChoices && (
              <div style={{ marginTop: 10, padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 8, maxWidth: 560 }}>
                <div style={{ fontSize: 12, color: 'var(--color-text-mute)', marginBottom: 6 }}>
                  This patient has {packetChoices.length} therapies — which one is the consent for?
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {packetChoices.map(t => (
                    <button key={t.id} disabled={busy} style={{ fontSize: 12, border: '1px solid var(--color-border)', background: 'transparent', borderRadius: 8, padding: '4px 10px', cursor: 'pointer' }}
                      onClick={() => printPacketFor(t.id, claimDocumentTab('consent'))}>
                      {t.drug_name || 'Drug not set'} <span style={{ color: 'var(--color-text-soft)' }}>· {t.state}</span>
                    </button>
                  ))}
                  <button style={{ fontSize: 12, border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--color-text-mute)' }}
                    onClick={() => setPacketChoices(null)}>Cancel</button>
                </div>
              </div>
            )}
            <button disabled={busy} style={{ ...primaryBtn, marginTop: 10 }} onClick={saveConsents}>{busy ? <Loader2 className="spin" size={14} /> : null} Save consents</button>
            <div style={{ ...muted, marginTop: 8 }}>E-signature capture (DocuSign) can be linked to a written consent — that integration is a follow-up on the existing e-sign workflow.</div>
          </div>

          {/* Fills / shipment */}
          <div style={card}>
            <div style={cardTitle}><Package size={14} /> Fill &amp; shipment</div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: 8, alignItems: 'end', marginBottom: 10 }}>
              <div><label style={lbl}>Drug</label><input style={input} value={fillForm.drug} onChange={e => setFillForm({ ...fillForm, drug: e.target.value })} /></div>
              <div><label style={lbl}>Quantity</label><input style={input} value={fillForm.quantity} onChange={e => setFillForm({ ...fillForm, quantity: e.target.value })} /></div>
              <div><label style={lbl}>Lot</label><input style={input} value={fillForm.lot} onChange={e => setFillForm({ ...fillForm, lot: e.target.value })} /></div>
              <div><label style={lbl}>NDC</label><input style={input} value={fillForm.ndc} onChange={e => setFillForm({ ...fillForm, ndc: e.target.value })} /></div>
              <button disabled={busy} style={primaryBtn} onClick={addFill}>Add fill</button>
            </div>
            {fills.length === 0 ? <div style={muted}>No fills yet.</div> : (
              <>{fillFx.chips}
              <div style={{ overflowX: 'auto' }}><table {...fillFx.tableProps} className="rwd-stack" style={{ width: '100%', minWidth: 'max-content', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead><tr>{['Drug', 'Qty / Lot', 'Status', 'Tracking', ''].map(h => <th key={h} style={th}>{h} {fillFx.funnel(h)}</th>)}</tr></thead>
                <tbody>
                  {fillFx.rows.map(f => {
                    const [fg, bg] = fStatusColor(f.fill_status)
                    return (
                      <Fragment key={f.id}>
                      <tr style={{ borderBottom: shipOpen === f.id ? 'none' : '1px solid var(--color-border)' }}>
                        <td data-label="Drug" style={td}>{f.drug || '—'}</td>
                        <td data-label="Qty / Lot" style={td}>{f.quantity || '—'}{f.lot ? ` · ${f.lot}` : ''}</td>
                        <td data-label="Status" style={td}><span style={{ fontSize: 11, fontWeight: 700, textTransform: 'capitalize', padding: '3px 9px', borderRadius: 999, color: fg, background: bg }}>{f.fill_status}</span></td>
                        <td data-label="Tracking" style={td}>{f.carrier ? `${f.carrier}${f.tracking_number ? ' · ' + f.tracking_number : ''}` : '—'}</td>
                        <td data-label="" style={{ ...td, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          {f.fill_status === 'pending' && <button disabled={busy} style={actBtn('#16a34a')} onClick={() => fillAction(f.id, 'confirm')}><CheckCircle2 size={13} /> Confirm</button>}
                          {f.fill_status === 'confirmed' && <button disabled={busy} style={actBtn('#0369a1')} onClick={() => { setShipOpen(shipOpen === f.id ? null : f.id); setShip({ carrier: '', tracking_number: '' }) }}><Truck size={13} /> Ship</button>}
                          {f.fill_status === 'shipped' && <button disabled={busy} style={actBtn('#15803d')} onClick={() => fillAction(f.id, 'deliver')}>Mark delivered</button>}
                          {f.fill_status !== 'delivered' && f.fill_status !== 'cancelled' && <button disabled={busy} style={actBtn('#b91c1c')} onClick={() => fillAction(f.id, 'cancel')}><XCircle size={13} /> Cancel</button>}
                        </td>
                      </tr>
                      {shipOpen === f.id && (
                        <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                          <td colSpan={5} style={{ padding: '4px 8px 12px' }}>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'end' }}>
                              <div><label style={lbl}>Carrier</label><input style={{ ...input, width: 160 }} value={ship.carrier} onChange={e => setShip({ ...ship, carrier: e.target.value })} placeholder="FedEx / UPS" /></div>
                              <div><label style={lbl}>Tracking #</label><input style={{ ...input, width: 220 }} value={ship.tracking_number} onChange={e => setShip({ ...ship, tracking_number: e.target.value })} /></div>
                              <button disabled={busy} style={actBtn('#0369a1')} onClick={() => fillAction(f.id, 'ship', { carrier: ship.carrier || undefined, tracking_number: ship.tracking_number || undefined })}><Truck size={13} /> Confirm shipment</button>
                            </div>
                          </td>
                        </tr>
                      )}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table></div></>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const card: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 12, padding: 14, background: 'var(--color-surface)' }
const cardTitle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, marginBottom: 10 }
const lbl: React.CSSProperties = { display: 'block', fontSize: 11, color: 'var(--color-text-mute)', marginBottom: 3 }
const input: React.CSSProperties = { width: '100%', padding: '7px 9px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, background: 'var(--color-surface)', color: 'var(--color-text)', boxSizing: 'border-box' }
const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: .4, color: 'var(--color-text-mute)', borderBottom: '2px solid var(--color-border)' }
const td: React.CSSProperties = { padding: '9px 10px' }
const muted: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-mute)', display: 'flex', alignItems: 'center', gap: 6 }
const primaryBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const linkDanger: React.CSSProperties = { background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer', fontSize: 12, fontWeight: 600, padding: 0, justifySelf: 'start' }
const actBtn = (color: string): React.CSSProperties => ({ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: `1px solid ${color}`, background: 'var(--color-surface)', color })
